<template>
  <div
    class="inline-block w-full p-4 border rounded shadow bg-white"
    :class="{
      'error': error,
    }"
  >
    <div class="flex justify-between items-center mb-2">
      <button type="button" @click="changeMonth(-1)" class="text-sm px-2">
        &lt;
      </button>
      <span class="text-sm font-semibold">{{ currentMonthYear }}</span>
      <button type="button" @click="changeMonth(1)" class="text-sm px-2">
        &gt;
      </button>
    </div>

    <div class="grid grid-cols-7 text-center text-xs text-gray-500 mb-1">
      <span v-for="d in weekDays" :key="d">{{ d }}</span>
    </div>

    <div class="grid grid-cols-7 gap-1 text-sm">
      <span
        v-for="(day, index) in calendarDays"
        :key="index"
        @click="selectDate(day)"
        :class="dayClass(day)"
      >
        {{ day.getDate() }}
      </span>
    </div>

    <div class="flex justify-between items-center mt-4 text-xs">
      <div class="text-gray-600">
        {{ displayLabel }}
      </div>
      <button
        type="button"
        @click="clear"
        class="text-gray-500 hover:text-red-600"
      >
        Clear
      </button>
    </div>
  </div>
</template>

<script>
function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function isAfterToday(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

export default {
  name: 'DateRangePicker',
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    error: {
      type: Boolean,
      default: false,
    },
    startKey: {
      type: String,
      default: 'start',
    },
    endKey: {
      type: String,
      default: 'end',
    },
  },
  data() {
    const today = new Date();
    return {
      currentDate: new Date(today.getFullYear(), today.getMonth(), 1),
      selecting: 'start',
    };
  },
  computed: {
    weekDays() {
      return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    },
    currentMonthYear() {
      return this.currentDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      });
    },
    calendarDays() {
      const start = new Date(this.currentDate);
      const days = [];

      const firstDay = new Date(start.getFullYear(), start.getMonth(), 1);
      const startWeekday = firstDay.getDay();
      firstDay.setDate(firstDay.getDate() - startWeekday);

      for (let i = 0; i < 42; i++) {
        const day = new Date(firstDay);
        day.setDate(firstDay.getDate() + i);
        days.push(day);
      }

      return days;
    },
    displayLabel() {
      const start = this.modelValue[this.startKey];
      const end = this.modelValue[this.endKey];
      if (start && end) return `${start} – ${end}`;
      if (start) return `From ${start}`;
      if (end) return `Until ${end}`;
      return 'Select date range';
    },
  },
  methods: {
    emitUpdate(newVal) {
      this.$emit('update:modelValue', newVal);
    },
    changeMonth(offset) {
      const newDate = new Date(this.currentDate);
      newDate.setMonth(newDate.getMonth() + offset);
      this.currentDate = newDate;
    },
    clear() {
      this.selecting = 'start';
      this.emitUpdate({
        ...this.modelValue,
        [this.startKey]: '',
        [this.endKey]: '',
      });
    },
    selectDate(date) {
      if (isAfterToday(date)) return;

      const iso = formatDate(date);
      let newValue = { ...this.modelValue };

      if (this.selecting === 'start') {
        newValue[this.startKey] = iso;
        newValue[this.endKey] = '';
        this.selecting = 'end';
      } else {
        if (new Date(iso) < new Date(newValue[this.startKey])) {
          newValue[this.endKey] = newValue[this.startKey];
          newValue[this.startKey] = iso;
        } else {
          newValue[this.endKey] = iso;
        }
        this.selecting = 'start';
      }

      this.emitUpdate(newValue);
    },
    dayClass(day) {
      const classes = ['rounded', 'py-1', 'text-center'];

      const iso = formatDate(day);
      const start = this.modelValue[this.startKey];
      const end = this.modelValue[this.endKey];
      const isDisabled = isAfterToday(day);

      if (isDisabled) {
        classes.push('text-gray-300', 'cursor-not-allowed', 'bg-gray-100');
      } else {
        classes.push('cursor-pointer');

        if (day.getMonth() !== this.currentDate.getMonth()) {
          classes.push('text-gray-300');
        } else {
          classes.push('text-gray-700');
        }

        if (iso === start || iso === end) {
          classes.push('bg-highlight', 'text-white');
        } else if (start && end && iso > start && iso < end) {
          classes.push('bg-lighthighlight', 'text-white');
        }
      }

      return classes.join(' ');
    },
  },
};
</script>

<style scoped>
.bg-lighthighlight {
  background-color: #cdc4a5;
}
.text-white {
  color: #ffffff;
}
.error {
  border: 1px solid #ff0000 !important;
}
</style>
